import type { admin } from '@repletoryreach/sdk-api';
import { chargeInForce } from '../../../lib/priceLadder';
import { formatMinor } from '../../../lib/format';
import type { Translate } from '../../../i18n/I18nProvider';

export type AiModelKind = admin.AdminAiModel['kind'];

/** The order the page reads in: what businesses use most first. */
export const KIND_ORDER: readonly AiModelKind[] = ['text', 'speech', 'transcribe', 'image'];

/** Every kind the page knows can be tested (14 October 2026); a kind added later waits for its own test. */
export const canTest = (m: admin.AdminAiModel): boolean => KIND_ORDER.includes(m.kind);

/** A picture test costs the platform a picture, so it waits for a click instead of running when the dialog opens. */
export const testCosts = (m: admin.AdminAiModel): boolean => m.kind === 'image';

/** Speech to text cannot be switched on until it names the S3 bucket recordings wait in. */
export const needsBucket = (m: admin.AdminAiModel): boolean => m.kind === 'transcribe' && !m.storage_bucket?.trim();

/** An S3 bucket name, as the service checks it. */
export const BUCKET_PATTERN = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;

/** The AWS region code the service accepts (its own check, repeated so the drawer can say so before saving). */
export const REGION_PATTERN = /^[a-z]{2}(-gov)?-[a-z]+-\d$/;

/** The service holds the longest answer between these. */
export const MAX_OUTPUT_MIN = 1;
export const MAX_OUTPUT_MAX = 64_000;
/** A text model's context window (30 October 2026): 0 for not known, otherwise this range, as the service checks it. */
export const CONTEXT_MIN = 4_000;
export const CONTEXT_MAX = 2_000_000;

type Rate = admin.AdminSkuRate;

/**
 * The rate a SKU is charged at today. Credits are rand, so the rand row in force is the price; a SKU priced only
 * in another currency still shows its row rather than nothing.
 */
export function rateInForce(rates: readonly Rate[], sku: string | null | undefined): Rate | undefined {
  if (!sku) return undefined;
  const current = rates.filter((r) => r.sku_code === sku && r.status === 'current');
  return current.find((r) => r.currency === 'ZAR') ?? current[0];
}

/** What a model costs per unit, from the SKUs it names. */
export interface ModelPrice {
  /** Per-token models: what it reads and what it writes. */
  read?: Rate;
  written?: Rate;
  /** Speech, transcription and pictures: one SKU per unit. */
  unit?: Rate;
  /** SKUs the model names that have no rate in force in the price book. */
  missing: string[];
  /** The model names no SKU at all, so nothing can charge for it. */
  unpriced: boolean;
}

export function modelPrice(m: admin.AdminAiModel, rates: readonly Rate[]): ModelPrice {
  const named = [m.in_sku, m.out_sku, m.unit_sku].filter((s): s is string => Boolean(s));
  const read = rateInForce(rates, m.in_sku);
  const written = rateInForce(rates, m.out_sku);
  const unit = rateInForce(rates, m.unit_sku);
  const found = new Set([read, written, unit].filter(Boolean).map((r) => r!.sku_code));
  return { read, written, unit, missing: named.filter((s) => !found.has(s)), unpriced: named.length === 0 };
}

const unitLabel = (t: Translate, unit: string) => t(`billing.skuRates.unit.${unit}`, unit);

/**
 * One line a person reads: "R0,02 read / R0,06 written per 10 000 tokens", or "R0,56 per 1 000 characters".
 * `figure` picks the list price (what the Free plan pays) or the platform's cost. Null when nothing is priced.
 */
export function priceLine(t: Translate, p: ModelPrice, figure: 'list' | 'cost' = 'list'): string | null {
  const money = (r: Rate) => formatMinor(figure === 'list' ? chargeInForce(r) : r.cost_minor, r.currency);
  const parts: string[] = [];
  if (p.read && p.written) {
    parts.push(p.read.unit === p.written.unit
      ? t('billing.aiModels.price.readWritten', { read: money(p.read), written: money(p.written), unit: unitLabel(t, p.read.unit) })
      : t('billing.aiModels.price.readWrittenUnits', {
        read: money(p.read), readUnit: unitLabel(t, p.read.unit),
        written: money(p.written), writtenUnit: unitLabel(t, p.written.unit)
      }));
  } else if (p.read) {
    parts.push(t('billing.aiModels.price.readOnly', { read: money(p.read), unit: unitLabel(t, p.read.unit) }));
  } else if (p.written) {
    parts.push(t('billing.aiModels.price.writtenOnly', { written: money(p.written), unit: unitLabel(t, p.written.unit) }));
  }
  if (p.unit) parts.push(t('billing.aiModels.price.unit', { price: money(p.unit), unit: unitLabel(t, p.unit.unit) }));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** An empty model id means the text model in the platform's own Bedrock settings. */
export const usesPlatformModel = (m: admin.AdminAiModel): boolean => !m.model_id.trim();

/** Where the model runs, in words: an empty region means the platform's own Bedrock region. */
export function regionText(t: Translate, m: admin.AdminAiModel): string {
  return m.region?.trim() ? m.region : t('billing.aiModels.region.platform');
}

/** Models in reading order: by kind, then the owner's sort order, then name. */
export function sortModels(models: readonly admin.AdminAiModel[]): admin.AdminAiModel[] {
  const order = new Map(KIND_ORDER.map((k, i) => [k, i]));
  return [...models].sort((a, b) =>
    (order.get(a.kind) ?? 99) - (order.get(b.kind) ?? 99)
    || a.sort_order - b.sort_order
    || a.name.localeCompare(b.name));
}
